import { useState } from 'react';
import {
  BusinessFormData,
  Dac7FormDataTypes,
  Dac7FormValidationResult,
  PrivateFormData,
  SoleTraderFormData,
} from '@olxeu-a2b/ui/typings/dac7';

const isDac7Private = (dac7Data: Dac7FormDataTypes): dac7Data is PrivateFormData =>
  dac7Data.type === 'PRIVATE';
const isDac7Business = (dac7Data: Dac7FormDataTypes): dac7Data is BusinessFormData =>
  dac7Data.type === 'BUSINESS';
const isDac7SoleTrader = (dac7Data: Dac7FormDataTypes): dac7Data is SoleTraderFormData =>
  dac7Data.type === 'SOLE_TRADER';

const getPrivateDataValidationResult = (
  data: Readonly<PrivateFormData>,
): Dac7FormValidationResult => {
  let res: Dac7FormValidationResult = [];
  if (!data.personalDetails.firstName) {
    res = [['firstName', 'dlv_dac7_validation_error_firstName']];
  }

  if (!data.personalDetails.lastName) {
    res = [...res, ['lastName', 'dlv_dac7_validation_error_lastName']];
  }

  if (!data.personalDetails.dateOfBirth) {
    res = [...res, ['birthDate', 'dlv_dac7_validation_error_birthDate']];
  }

  if (
    !Array.isArray(data.taxDetails.taxNumbersDetails) ||
    data.taxDetails.taxNumbersDetails.length === 0
  ) {
    res = [...res, ['taxDetails', 'dlv_dac7_validation_error_taxDetails']];
  }

  if (!data.address.cityName || !data.address.streetName || !data.address.streetNumber) {
    res = [...res, ['address', 'dlv_dac7_validation_error_addresses']];
  }

  return res;
};

const getBusinessDataValidationResult = (
  data: Readonly<BusinessFormData>,
): Dac7FormValidationResult => {
  let res: Dac7FormValidationResult = [];
  if (!data.businessDetails.companyName) {
    res = [['legalName', 'dlv_dac7_validation_error_legalName']];
  }

  if (
    !Array.isArray(data.taxDetails.taxNumbersDetails) ||
    data.taxDetails.taxNumbersDetails.length === 0
  ) {
    res = [...res, ['taxDetails', 'dlv_dac7_validation_error_taxDetails']];
  }

  return res;
};

const getSoleTraderValidationResult = (
  data: Readonly<SoleTraderFormData>,
): Dac7FormValidationResult => {
  let res: Dac7FormValidationResult = [];
  if (!data.personalDetails.firstName) {
    res = [['firstName', 'dlv_dac7_validation_error_firstName']];
  }

  if (!data.personalDetails.lastName) {
    res = [...res, ['lastName', 'dlv_dac7_validation_error_lastName']];
  }

  if (!data.personalDetails.dateOfBirth) {
    res = [...res, ['birthDate', 'dlv_dac7_validation_error_birthDate']];
  }

  if (
    !Array.isArray(data.taxDetails.taxNumbersDetails) ||
    data.taxDetails.taxNumbersDetails.length === 0
  ) {
    res = [...res, ['taxDetails', 'dlv_dac7_validation_error_taxDetails']];
  }

  if (!data.address.cityName || !data.address.streetName || !data.address.streetNumber) {
    res = [...res, ['address', 'dlv_dac7_validation_error_addresses']];
  }

  return res;
};

// validates whatever was problematic to validate inline in the form
const useDac7Validate = () => {
  const [validationResult, setValidationResult] = useState<Dac7FormValidationResult>([]);

  const validate = (
    data: Readonly<Dac7FormDataTypes & { termsAndConditions?: string }>,
  ): boolean => {
    let res: Dac7FormValidationResult = [];

    if (data.termsAndConditions !== 'on') {
      res = [...res, ['termsAndConditions', 'dlv_dac7_validation_error_t&c']];
    }

    if (isDac7Private(data)) {
      res = [...res, ...getPrivateDataValidationResult(data)];
    } else if (isDac7Business(data)) {
      res = [...res, ...getBusinessDataValidationResult(data)];
    } else if (isDac7SoleTrader(data)) {
      res = [...res, ...getSoleTraderValidationResult(data)];
    } else {
      res = [...res, ['type', 'dlv_dac7_validation_invalid_type']];
    }

    setValidationResult(res);
    return res.length === 0;
  };

  return {
    validate,
    validationResult,
  };
};

export default useDac7Validate;
